import * as turf from "@turf/turf";
import mapboxgl, { Map } from "mapbox-gl";
import MapVisConfig from "./MapVisConfig";
import PoISelect from "../gui/controlwrappers/PoISelect";
import DataHandler from "../data/DataHandler.js";
import {
  GenericJavaScriptObject,
  IsochroneLayer,
} from "../../interfaces/map.interfaces";

export default class IsochroneHandler {
  #map!: any;
  #layerConfig;

  #datahandler!: DataHandler;

  #poiguicontroller;

  #previousRange?: number;
  #previousLocation: number[] = [];
  #popups!: GenericJavaScriptObject<any>;
  #isochrones!: IsochroneLayer[];
  #selectedIsochrone: any; //GUI selected elements
  #tangiblePoint: any; // drawn lng, lat of IsochroneLocation

  constructor(datahandler: DataHandler) {
    // @ts-ignore
    if (IsochroneHandler.instance) {
      // @ts-ignore
      return IsochroneHandler.instance;
    }
    this.#map = $("#mapghost").data() as Map;
    // @ts-ignore
    this.#map = this.#map.o.getMap();
    this.#layerConfig = new MapVisConfig().getLayerConfig();

    this.#datahandler = datahandler;

    this.#previousRange = 0;
    this.#previousLocation = [0, 0];
    this.#popups = {};
    this.#isochrones = [];
    this.#poiguicontroller = new PoISelect();
    this.#poiguicontroller.registerEventListener((event: any) =>
      this.handlePoISelectEvent(event)
    );
  }

  setMap(map: Map) {
    this.#map = map;
  }

  getIsochrones() {
    this.#isochrones = this.#map
      .getStyle()
      .layers.filter((layer: any) =>
        layer.id.includes("isochrone")
      ) as IsochroneLayer[];
    var isochroneIds = this.#isochrones.map((layer) => layer.id);
    var isochroneSources = isochroneIds.map(
      // @ts-ignore
      (id) => this.#map.getSource(id)._options.data
    );
    isochroneSources.forEach((source, index) => {
      source.id = isochroneIds[index];
    });
    return isochroneSources;
  }

  removeIsochrone(id: string) {
    if (id.includes("location")) {
      this.#tangiblePoint = "";
    }
    if (this.#map.getLayer(id)) {
      this.#map.removeLayer(id);
    }
    if (this.#map.getSource(id)) {
      this.#map.removeSource(id);
    }
  }

  removePopup(id: string) {
    if (this.#popups[id]) this.#popups[id].remove();
  }

  angleToSecondMapper(angle: number) {
    var angleRanges = [
      [0, 0.5177810192108154],
      [0.5177810192108154, 1.0355620384216308],
      [1.0355620384216308, 1.5533430576324462],
      [1.5533430576324462, 2.0711240768432615],
      [2.0711240768432615, 2.588905096054077],
      [2.588905096054077, 3.1066861152648924],
      [3.1066861152648924, 3.6244671344757078],
      [3.6244671344757078, 4.142248153686523],
      [4.142248153686523, 4.660029172897338],
      [4.660029172897338, 5.177810192108154],
      [5.177810192108154, 5.695591211318969],
      [5.695591211318969, 7],
    ];
    var secondRanges = [
      1200, 1500, 1800, 2100, 2400, 2700, 3000, 3300, 3600, 300, 600, 900,
    ];

    for (var i = 0; i < angleRanges.length; i++) {
      if (angle >= angleRanges[i][0] && angle < angleRanges[i][1]) {
        return secondRanges[i];
      } else {
        return undefined;
      }
    }
  }

  rangeToIntervalMapper(range: number) {
    if (range <= 2700) return 300;
    else if (range > 2700 && range <= 3000) return 600;
    else if (range > 3000 && range <= 3300) return 825;
    else if (range > 3300 && range <= 3600) return 900;
  }

  secondToMinuteMapper(second: number) {
    if (second % 60 == 0) {
      return second / 60;
    }
  }

  findFarthestCoordinates(referencePoint: any, coordinates: any) {
    let farthestDistance = 0;
    let farthestCoordinates;

    coordinates.forEach((coord: any) => {
      const distance = turf.distance(referencePoint, turf.point(coord));

      if (distance > farthestDistance) {
        farthestDistance = distance;
        farthestCoordinates = coord;
      }
    });

    return farthestCoordinates;
  }

  async handlePoISelectEvent(event: any) {
    if (event.action == "remove") {
      var isochrones = this.getIsochrones();
      isochrones = isochrones.filter((isochrone) =>
        isochrone.id.includes("isochronePoi")
      );
      isochrones.forEach((isochrone) => {
        this.removeIsochrone(isochrone.id);
      });
    } else {
      var location = $("#guighost").data("obj").getSelectedLocationID();
      var aggregation = $("#guighost").data("obj").getAggregation();

      if (
        aggregation == "municipality-square" ||
        aggregation == "municipality-quarter"
      ) {
        const municipalities =
          await this.#datahandler.requestNeighbouringMunicipalities({
            p_municipality: location,
          });
        var range =
          event.metric === "time" ? parseInt(event.value) * 60 : event.value;
        var metric = event.metric == "time" ? "sec" : "meter";
        var request = {
          p_municipality: municipalities.map((item) => item.bfsid),
          p_poi: event.poi,
          p_profile: event.profile,
          p_metric: metric,
          p_reachability: range,
        };
        if (this.#map.getLayer("isochronePoi") != null) {
          this.removeIsochrone("isochronePoi");
        }
        const isochrones = await this.#datahandler.requestIsochronePoi(request);

        if (isochrones[0].geojson.features == null) {
          this.removeIsochrone("isochronePoi");
          return;
        } else if (this.#map.getLayer("isochronePoi") == null) {
          if (this.#tangiblePoint) {
            this.#datahandler.requestIsochroneUpToInterval(
              {
                p_profile: event.profile,
                p_location: [this.#tangiblePoint.lng, this.#tangiblePoint.lat],
                p_range: range,
                p_metric: metric,
              },
              (isochrone: any) => {
                this.#datahandler.removeWaters(
                  {
                    p_isochrone: isochrone,
                  },
                  (newIsochrone: any) => {
                    var oldIsochrone = this.getIsochrones()[0];
                    var newIsochrone = newIsochrone[0].result;
                    this.#datahandler.mergeIsochrones(
                      {
                        p_oldIsochrones: oldIsochrone,
                        p_newIsochrone: newIsochrone,
                        p_profile: event.profile,
                        p_metric: metric,
                        p_reachability: range,
                      },
                      (mergedIsochrone: any[]) => {
                        if (mergedIsochrone[0].geojson.features == null) {
                          mergedIsochrone = oldIsochrone;
                        }
                        this.removeIsochrone("isochronePoi");
                        if (
                          mergedIsochrone[0].geojson.metadata.metric == "sec"
                        ) {
                          const srcAndLayerName = "IsochronePoiSec";
                          var config =
                            this.#layerConfig[srcAndLayerName].layerTemplate;
                          config.source = {
                            type: "geojson",
                            data: mergedIsochrone[0].geojson,
                          };
                        } else if (
                          mergedIsochrone[0].geojson.metadata.metric == "meter"
                        ) {
                          const srcAndLayerName = "IsochronePoiMeter";
                          var config =
                            this.#layerConfig[srcAndLayerName].layerTemplate;
                          config.source = {
                            type: "geojson",
                            data: mergedIsochrone[0].geojson,
                          };
                        }
                        this.#map.addLayer(config);
                      }
                    );
                  }
                );
              }
            );
          }
          if (isochrones[0].geojson.metadata.metric == "sec") {
            const srcAndLayerName = "IsochronePoiSec";
            var config = this.#layerConfig[srcAndLayerName].layerTemplate;
            config.source = {
              type: "geojson",
              data: isochrones[0].geojson,
            };
            this.#map.addLayer(config);
          } else if (isochrones[0].geojson.metadata.metric == "meter") {
            var srcAndLayerName = "IsochronePoiMeter";

            var config = this.#layerConfig[srcAndLayerName].layerTemplate;
            config.source = {
              type: "geojson",
              data: isochrones[0].geojson,
            };
            this.#map.addLayer(config);
          }
        }
        this.#selectedIsochrone = this.getIsochrones()[0];
      } else if (
        aggregation == "canton-municipality" ||
        aggregation == "canton-district"
      ) {
        this.#datahandler.requestCantonsMunicipalities(
          { p_canton: location },
          async (municipalities: { bfsid: number[] }[]) => {
            console.log(municipalities);
            var range =
              event.metric === "time"
                ? parseInt(event.value) * 60
                : event.value;
            var metric = event.metric == "time" ? "sec" : "meter";
            var request = {
              p_municipality: municipalities.map((item: any) => item.bfsids)[0],
              p_poi: event.poi,
              p_profile: event.profile,
              p_metric: metric,
              p_reachability: range,
            };
            if (this.#map.getLayer("isochronePoi") != null) {
              this.removeIsochrone("isochronePoi");
            }
            const isochrones = await this.#datahandler.requestIsochronePoi(
              request
            );

            if (isochrones[0].geojson.features == null) {
              this.removeIsochrone("isochronePoi");
              return;
            } else if (this.#map.getLayer("isochronePoi") == null) {
              if (this.#tangiblePoint) {
                this.#datahandler.requestIsochroneUpToInterval(
                  {
                    p_profile: event.profile,
                    p_location: [
                      this.#tangiblePoint.lng,
                      this.#tangiblePoint.lat,
                    ],
                    p_range: range,
                    p_metric: metric,
                  },
                  (isochrone: any) => {
                    this.#datahandler.removeWaters(
                      {
                        p_isochrone: isochrone,
                      },
                      (newIsochrone: any) => {
                        var oldIsochrone = this.getIsochrones()[0];
                        var newIsochrone = newIsochrone[0].result;
                        this.#datahandler.mergeIsochrones(
                          {
                            p_oldIsochrones: oldIsochrone,
                            p_newIsochrone: newIsochrone,
                            p_profile: event.profile,
                            p_metric: metric,
                            p_reachability: range,
                          },
                          (mergedIsochrone: any[]) => {
                            if (mergedIsochrone[0].geojson.features == null) {
                              mergedIsochrone = oldIsochrone;
                            }
                            this.removeIsochrone("isochronePoi");
                            if (
                              mergedIsochrone[0].geojson.metadata.metric ==
                              "sec"
                            ) {
                              const srcAndLayerName = "IsochronePoiSec";
                              var config =
                                this.#layerConfig[srcAndLayerName]
                                  .layerTemplate;
                              config.source = {
                                type: "geojson",
                                data: mergedIsochrone[0].geojson,
                              };
                            } else if (
                              mergedIsochrone[0].geojson.metadata.metric ==
                              "meter"
                            ) {
                              var srcAndLayerName = "IsochronePoiMeter";

                              var config =
                                this.#layerConfig[srcAndLayerName]
                                  .layerTemplate;
                              config.source = {
                                type: "geojson",
                                data: mergedIsochrone[0].geojson,
                              };
                            }
                            this.#map.addLayer(config);
                          }
                        );
                      }
                    );
                  }
                );
              }
              if (isochrones[0].geojson.metadata.metric == "sec") {
                const srcAndLayerName = "IsochronePoiSec";
                var config = this.#layerConfig[srcAndLayerName].layerTemplate;
                config.source = {
                  type: "geojson",
                  data: isochrones[0].geojson,
                };
                this.#map.addLayer(config);
              } else if (isochrones[0].geojson.metadata.metric == "meter") {
                var srcAndLayerName = "IsochronePoiMeter";

                var config = this.#layerConfig[srcAndLayerName].layerTemplate;
                config.source = {
                  type: "geojson",
                  data: isochrones[0].geojson,
                };
                this.#map.addLayer(config);
              }
            }
            this.#selectedIsochrone = this.getIsochrones()[0];
          }
        );
      }
    }
  }

  computeIsochrone(
    id: string,
    profile: any,
    metric: any,
    range: any,
    relativeX: number,
    relativeY: number
  ) {
    var locationId = "location" + id;

    var screenX = relativeX * $(this.#map._container).width()!;
    var screenY = relativeY * $(this.#map._container).height()!;
    console.log(profile, metric, range, relativeX, relativeY);
    var projected: any = this.#map.unproject(
      new mapboxgl.Point(screenX, screenY)
    );
    projected.locationId = locationId;

    this.#tangiblePoint = projected;

    this.#map.addLayer({
      id: locationId,
      type: "circle",
      source: {
        type: "geojson",
        data: {
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: [projected.lng, projected.lat],
          },
        },
      },
      paint: {
        "circle-radius": 10,
        "circle-color": "#5e605a",
      },
    });

    this.#datahandler.requestIsochroneUpToInterval(
      {
        p_profile: profile,
        p_metric: metric,
        p_range: range,
        p_location: [projected.lng, projected.lat],
      },
      (isochrone: any) => {
        this.#datahandler.removeWaters(
          {
            p_isochrone: isochrone,
          },
          (newIsochrone: any) => {
            this.#selectedIsochrone = this.getIsochrones()[0];
            var oldIsochrone = this.getIsochrones()[0];
            var newIsochrone = newIsochrone[0].result;

            this.#datahandler.mergeIsochrones(
              {
                p_oldIsochrones: oldIsochrone,
                p_newIsochrone: newIsochrone,
                p_profile: profile,
                p_metric: metric,
                p_reachability: range,
              },
              (mergedIsochrone: any[]) => {
                this.removeIsochrone("isochronePoi");
                if (mergedIsochrone[0].geojson.metadata.metric == "sec") {
                  const srcAndLayerName = "IsochronePoiSec";
                  var config = this.#layerConfig[srcAndLayerName].layerTemplate;
                  config.source = {
                    type: "geojson",
                    data: mergedIsochrone[0].geojson,
                  };
                } else if (
                  mergedIsochrone[0].geojson.metadata.metric == "meter"
                ) {
                  const srcAndLayerName = "IsochronePoiMeter";
                  var config = this.#layerConfig[srcAndLayerName].layerTemplate;
                  config.source = {
                    type: "geojson",
                    data: mergedIsochrone[0].geojson,
                  };
                }
                this.#map.addLayer(config);
              }
            );
          }
        );
      }
    );
  }

  // Methods below for the IsochronePainter tangible => NEEDS REWORK!
  drawPoiIsochrone() {
    var isochornePoi = this.#selectedIsochrone;

    if (isochornePoi.metadata.metric == "sec") {
      this.#map.addLayer({
        id: "isochronePoi",
        type: "fill",
        source: {
          type: "geojson",
          data: isochornePoi,
        },
        paint: {
          "fill-color": [
            "interpolate",
            ["linear"],
            ["get", "reachability"], // Property containing the reachability value
            300,
            "#fde725",
            600,
            "#c2df23",
            900,
            "#86d549",
            1200,
            "#52c569",
            1500,
            "#2ab07f",
            1800,
            "#1e9b8a",
            2100,
            "#25858e",
            2400,
            "#2d708e",
            2700,
            "#38588c",
            3000,
            "#433e85",
            3300,
            "#482173",
            3600,
            "#440154",
          ],
          "fill-opacity": 0.5,
        },
        layout: {
          visibility: "visible", // Set the visibility of the outline to 'visible'
        },
        before: "waterway-label",
      });
    } else if (isochornePoi.metadata.metric == "meter") {
      this.#map.addLayer({
        id: "isochronePoi",
        type: "fill",
        source: {
          type: "geojson",
          data: isochornePoi,
        },
        paint: {
          "fill-color": [
            "interpolate",
            ["linear"],
            ["get", "reachability"], // Property containing the reachability value
            100,
            "#fde725",
            200,
            "#c2df23",
            300,
            "#86d549",
            400,
            "#52c569",
            500,
            "#2ab07f",
            750,
            "#1e9b8a",
            1000,
            "#25858e",
            1500,
            "#2d708e",
            2000,
            "#38588c",
            3000,
            "#433e85",
            4000,
            "#482173",
            5000,
            "#440154",
          ],
          "fill-opacity": 0.5,
        },
        layout: {
          visibility: "visible", // Set the visibility of the outline to 'visible'
        },
        before: "waterway-label",
      });
    }
  }

  drawIsochrone(id: string, x: number, y: number, angle: number) {
    var screenX = x * $(this.#map._container).width()!;
    var screenY = y * $(this.#map._container).height()!;
    var projected = this.#map.unproject(new mapboxgl.Point(screenX, screenY));

    var range = this.angleToSecondMapper(angle);
    if (range) {
      var interval = this.rangeToIntervalMapper(range);
    }

    if (
      this.#previousRange != range ||
      (this.#previousLocation[0] != projected.lng &&
        this.#previousLocation[1] != projected.lat) ||
      false
    ) {
      this.#datahandler.requestIsochrone(
        {
          p_locations: [projected.lng, projected.lat],
          p_range: range,
          p_interval: interval,
        },
        (isochrone: any) => {
          var layerId = "isochrone" + id;

          if (this.#map.getLayer(layerId)) this.#map.removeLayer(layerId);
          if (this.#map.getSource(layerId)) this.#map.removeSource(layerId);

          this.#map.addLayer({
            id: layerId,
            type: "fill",
            source: {
              type: "geojson",
              data: isochrone,
            },
            paint: {
              "fill-color": "#e8fc03",
              "fill-outline-color": "#000000",
              "fill-opacity": 0.25,
            },
            before: "waterway-label",
          });
        }
      );
    }
    this.#previousLocation = [projected.lng, projected.lat];
    this.#previousRange = range;
    this.#isochrones = this.getIsochrones();
  }
}
